import React, { useEffect, useMemo, useState } from "react";
import { useCurrentUserQuery } from "../../../../api/queries/useUserQuery";
import { HTTP_ENDPOINTS, USER_ROLE_NAMES } from "../../../../consts/api";
import { http } from "../../../../services/HttpService";
import { uiNotification } from "../../../../services/UINotificationService";
import { CircularProgress } from "../../../../shared/CircularProgress/CircularProgress";
import styles from "./boomrx.module.scss";

const PAGE_SIZE = 40;

function errorMessage(err, fallback) {
  const data = err && err.response && err.response.data;
  if (data && data.data && data.data.detail) {
    return data.data.detail;
  }
  if (data && data.message && data.message.indexOf("wellsync_") !== 0) {
    return data.message;
  }
  return fallback;
}

function money(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "—";
  return `$${n.toFixed(2)}`;
}

function percent(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return null;
  return `${n}%`;
}

function groupMostExpensive(rows) {
  const grouped = new Map();
  rows.forEach((row) => {
    const drugName = String(row.drugName || "").trim();
    if (!drugName) return;
    const concentration = String(row.concentration || "").trim();
    const unit = String(row.unit || "").trim();
    const key = [drugName, concentration, unit].join("\u0000").toLowerCase();
    const price = Number(row.price);
    const existing = grouped.get(key);
    if (!existing) {
      grouped.set(key, {
        drugName,
        concentration,
        unit,
        price: Number.isFinite(price) ? price : null,
      });
      return;
    }
    if (
      Number.isFinite(price) &&
      (existing.price == null || price > existing.price)
    ) {
      existing.price = price;
    }
  });
  return Array.from(grouped.values());
}

function compareRows(sort, a, b) {
  if (sort === "cheap") {
    return (
      (a.price == null ? Infinity : a.price) -
        (b.price == null ? Infinity : b.price) ||
      a.drugName.localeCompare(b.drugName)
    );
  }
  if (sort === "name") {
    return (
      a.drugName.localeCompare(b.drugName) ||
      a.concentration.localeCompare(b.concentration)
    );
  }
  return (
    (b.price == null ? -1 : b.price) - (a.price == null ? -1 : a.price) ||
    a.drugName.localeCompare(b.drugName)
  );
}

export function BoomRXPriceList() {
  const { data: user, isLoading: userLoading } = useCurrentUserQuery();
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState(null);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("expensive");
  const [unit, setUnit] = useState("");
  const [page, setPage] = useState(0);

  useEffect(() => {
    if (user?.role !== USER_ROLE_NAMES.admin) return undefined;
    let cancelled = false;
    setLoading(true);
    http
      .get(HTTP_ENDPOINTS.boomrx.priceList())
      .then((res) => {
        if (cancelled) return;
        setList((res.data && res.data.data) || null);
      })
      .catch((err) => {
        if (cancelled) return;
        setList(null);
        uiNotification.error(errorMessage(err, "Failed to load BoomRX prices"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.role]);

  const rows = list && Array.isArray(list.rows) ? list.rows : [];
  const drugs = useMemo(() => groupMostExpensive(rows), [rows]);
  const units = useMemo(() => {
    const found = new Set();
    drugs.forEach((row) => {
      if (row.unit) found.add(row.unit);
    });
    return Array.from(found).sort((a, b) => a.localeCompare(b));
  }, [drugs]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return drugs
      .filter((row) => {
        if (unit && row.unit !== unit) return false;
        if (!q) return true;
        return [row.drugName, row.concentration, row.unit]
          .join(" ")
          .toLowerCase()
          .includes(q);
      })
      .sort((a, b) => compareRows(sort, a, b));
  }, [drugs, query, sort, unit]);
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const visible = filtered.slice(
    safePage * PAGE_SIZE,
    safePage * PAGE_SIZE + PAGE_SIZE,
  );
  const cardFee = percent(list && list.creditCardFeePercent);
  const convenienceFee = percent(list && list.convenienceFeePercent);

  if (userLoading || user?.role !== USER_ROLE_NAMES.admin) {
    return null;
  }

  return (
    <section className={styles.priceList}>
      <h3 className={styles.dashboardSectionTitle}>Drug price list</h3>
      <p className={styles.priceListLead}>
        BoomRX medication prices for this account. Each row is the highest price
        for that medication. BoomRX assigns the pharmacy.
        {cardFee || convenienceFee
          ? ` Card fee ${cardFee || "—"}. Convenience fee ${
              convenienceFee || "—"
            }.`
          : ""}
      </p>
      {loading ? (
        <CircularProgress />
      ) : list && list.connected === false ? (
        <p className={styles.dashboardEmpty}>
          Connect a BoomRX clinic to load prices.
        </p>
      ) : (
        <>
          <div className={styles.priceFilters}>
            <input
              className={styles.input}
              type="search"
              placeholder="Search medication or concentration"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setPage(0);
              }}
            />
            <select
              className={styles.input}
              value={sort}
              onChange={(e) => {
                setSort(e.target.value);
                setPage(0);
              }}
            >
              <option value="expensive">Most expensive</option>
              <option value="cheap">Least expensive</option>
              <option value="name">Name A–Z</option>
            </select>
            <select
              className={styles.input}
              value={unit}
              onChange={(e) => {
                setUnit(e.target.value);
                setPage(0);
              }}
            >
              <option value="">All units</option>
              {units.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </div>
          <p className={styles.priceListCount}>
            {filtered.length} medication{filtered.length === 1 ? "" : "s"}
            {query.trim() ? ` matching “${query.trim()}”` : ""}
          </p>
          {visible.length === 0 ? (
            <p className={styles.dashboardEmpty}>
              No medications match these filters.
            </p>
          ) : (
            <div className={styles.priceTableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Medication</th>
                    <th>Concentration</th>
                    <th>Highest price</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((row) => (
                    <tr
                      key={`${row.drugName}-${row.concentration}-${row.unit}`}
                    >
                      <td>{row.drugName}</td>
                      <td>
                        {row.concentration || "—"}
                        {row.unit ? ` (${row.unit})` : ""}
                      </td>
                      <td>{money(row.price)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {filtered.length > PAGE_SIZE ? (
            <div className={styles.pricePager}>
              <button
                type="button"
                className={styles.btnGhost}
                disabled={safePage === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                Previous
              </button>
              <span>
                Page {safePage + 1} of {pageCount}
              </span>
              <button
                type="button"
                className={styles.btnGhost}
                disabled={safePage >= pageCount - 1}
                onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
              >
                Next
              </button>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}
